/** Dot-path lookup and `{var}` interpolation for message catalogs. */

export type MessageVars = Record<string, string | number>;

/**
 * Walk a nested messages object by a dotted path (e.g. `"welcome.begin"`).
 * Returns the string at that path, or `undefined` if missing / not a string.
 */
export function getMessagePath(
  messages: unknown,
  path: string
): string | undefined {
  if (!path) return undefined;
  let cur: unknown = messages;
  for (const part of path.split(".")) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return typeof cur === "string" ? cur : undefined;
}

/**
 * Replace `{name}` placeholders with values from `vars`.
 * Always runs — a missing `vars` map is treated as empty.
 * Unknown placeholders are left unchanged so a forgotten call-site
 * value is visible in the string, not silently dropped.
 */
export function interpolate(
  template: string,
  vars?: MessageVars
): string {
  const map = vars ?? {};
  return template.replace(/\{(\w+)\}/g, (match, key: string) => {
    if (Object.prototype.hasOwnProperty.call(map, key)) {
      return String(map[key]);
    }
    return match;
  });
}
